// Nine Lives '84 - difficulty tables.
//
// Four settings as in the original (Kitten, House Cat, Tomcat, and a top one
// the original named after itself, called Stray here), then a gentle ramp per
// level up to the original's cap of 30. Speeds are units per second; intervals
// are ticks; ranges are [min, max].

import { Difficulty } from './types';

export interface DifficultyParams {
  lives: number;
  /** Dog patrol speed and the speed it charges a cat on the street. */
  dogSpeed: number;
  dogCharge: number;
  /** How long the dog stays off screen between passes. */
  dogAway: [number, number];
  mouseEvery: [number, number];
  mouseSpeed: number;
  /** Per-tick chance a closed window opens (while fewer than maxOpen are). */
  windowOpenChance: number;
  windowOpenTicks: [number, number];
  maxOpen: number;
  junkEvery: [number, number];
  /** Ticks Tabs may stand on the rival's can before being swatted. */
  rivalPatience: number;
  /** How fast the clotheslines run (units/s); they carry whoever stands on them. */
  lineDrift: number;
  // Rooms.
  broomSpeed: number;
  /** Ticks after a shove before the broom can shove again. */
  broomCooldown: number;
  roomMouseEvery: [number, number];
  /** How long a cheese mouse peeks out. */
  mousePeek: [number, number];
  cheeseMice: number;
  spiderSpeed: number;
  /** Ticks the spider rests at the ceiling between drops. */
  spiderRest: number;
  /** Nudges to push the birdcage off the table. */
  cageNudges: number;
  birdSpeed: number;
  /** Per-tick rise of a sleeping dog's wake meter while Tabs is close. */
  wakeRate: number;
  /** Per-tick fall of the meter when he is not. */
  wakeDecay: number;
  /** Ticks a woken dog stays up before going back to its spot. */
  awakeTicks: number;
  /** Ticks of drinking to empty a bowl. */
  drinkTicks: number;
  refillEvery: [number, number];
  fishCount: number;
  fishSpeed: number;
  eelCount: number;
  eelSpeed: number;
  /** Ticks of air underwater. */
  airTicks: number;
  /** Ticks between cupid arrows (each cupid). */
  cupidEvery: [number, number];
  arrowSpeed: number;
  brotherSpeed: number;
  giftEvery: [number, number];
  /** Ticks a bribed brother leaves Tabs alone. */
  bribeTicks: number;
  /** How many of Clementine's six brothers guard the rows (the top rows first). */
  brothers: number;
}

const BASE: DifficultyParams[] = [
  {
    lives: 9,
    dogSpeed: 38,
    dogCharge: 70,
    dogAway: [240, 480],
    mouseEvery: [360, 600],
    mouseSpeed: 40,
    windowOpenChance: 0.0035,
    windowOpenTicks: [360, 660],
    maxOpen: 3,
    junkEvery: [420, 720],
    rivalPatience: 150,
    lineDrift: 14,
    broomSpeed: 55,
    broomCooldown: 60,
    roomMouseEvery: [90, 150],
    mousePeek: [150, 240],
    cheeseMice: 3,
    spiderSpeed: 70,
    spiderRest: 180,
    cageNudges: 3,
    birdSpeed: 50,
    wakeRate: 1 / 200,
    wakeDecay: 1 / 60,
    awakeTicks: 200,
    drinkTicks: 150,
    refillEvery: [720, 960],
    fishCount: 4,
    fishSpeed: 30,
    eelCount: 1,
    eelSpeed: 40,
    airTicks: 720,
    cupidEvery: [560, 880],
    arrowSpeed: 55,
    brotherSpeed: 28,
    giftEvery: [300, 480],
    bribeTicks: 600,
    brothers: 3,
  },
  {
    lives: 6,
    dogSpeed: 48,
    dogCharge: 90,
    dogAway: [180, 360],
    mouseEvery: [240, 420],
    mouseSpeed: 52,
    windowOpenChance: 0.003,
    windowOpenTicks: [300, 540],
    maxOpen: 2,
    junkEvery: [300, 540],
    rivalPatience: 110,
    lineDrift: 18,
    broomSpeed: 70,
    broomCooldown: 50,
    roomMouseEvery: [80, 130],
    mousePeek: [120, 200],
    cheeseMice: 3,
    spiderSpeed: 90,
    spiderRest: 140,
    cageNudges: 3,
    birdSpeed: 62,
    wakeRate: 1 / 150,
    wakeDecay: 1 / 70,
    awakeTicks: 240,
    drinkTicks: 130,
    refillEvery: [560, 780],
    fishCount: 5,
    fishSpeed: 40,
    eelCount: 2,
    eelSpeed: 50,
    airTicks: 600,
    cupidEvery: [300, 480],
    arrowSpeed: 80,
    brotherSpeed: 38,
    giftEvery: [380, 560],
    bribeTicks: 480,
    brothers: 4,
  },
  {
    lives: 4,
    dogSpeed: 58,
    dogCharge: 110,
    dogAway: [120, 300],
    mouseEvery: [180, 330],
    mouseSpeed: 64,
    windowOpenChance: 0.0025,
    windowOpenTicks: [240, 420],
    maxOpen: 2,
    junkEvery: [220, 420],
    rivalPatience: 80,
    lineDrift: 24,
    broomSpeed: 85,
    broomCooldown: 40,
    roomMouseEvery: [70, 110],
    mousePeek: [100, 170],
    cheeseMice: 4,
    spiderSpeed: 110,
    spiderRest: 100,
    cageNudges: 4,
    birdSpeed: 76,
    wakeRate: 1 / 110,
    wakeDecay: 1 / 80,
    awakeTicks: 280,
    drinkTicks: 115,
    refillEvery: [440, 620],
    fishCount: 6,
    fishSpeed: 50,
    eelCount: 2,
    eelSpeed: 62,
    airTicks: 500,
    cupidEvery: [180, 300],
    arrowSpeed: 100,
    brotherSpeed: 50,
    giftEvery: [460, 660],
    bribeTicks: 380,
    brothers: 5,
  },
  {
    lives: 3,
    dogSpeed: 70,
    dogCharge: 135,
    dogAway: [90, 240],
    mouseEvery: [140, 260],
    mouseSpeed: 78,
    windowOpenChance: 0.002,
    windowOpenTicks: [180, 360],
    maxOpen: 1,
    junkEvery: [160, 330],
    rivalPatience: 60,
    lineDrift: 30,
    broomSpeed: 100,
    broomCooldown: 35,
    roomMouseEvery: [60, 100],
    mousePeek: [80, 140],
    cheeseMice: 4,
    spiderSpeed: 130,
    spiderRest: 80,
    cageNudges: 4,
    birdSpeed: 90,
    wakeRate: 1 / 85,
    wakeDecay: 1 / 90,
    awakeTicks: 320,
    drinkTicks: 100,
    refillEvery: [340, 500],
    fishCount: 7,
    fishSpeed: 60,
    eelCount: 3,
    eelSpeed: 75,
    airTicks: 420,
    cupidEvery: [110, 190],
    arrowSpeed: 120,
    brotherSpeed: 62,
    giftEvery: [560, 800],
    bribeTicks: 300,
    brothers: 6,
  },
];

export const MAX_LEVEL = 30;

export function difficultyParams(d: Difficulty, level: number): DifficultyParams {
  const b = BASE[d] ?? BASE[Difficulty.HOUSE_CAT];
  const s = 1 + 0.03 * (Math.min(Math.max(level, 1), MAX_LEVEL) - 1);
  const faster = (r: [number, number]): [number, number] => [
    Math.round(r[0] / s),
    Math.round(r[1] / s),
  ];
  return {
    lives: b.lives,
    dogSpeed: b.dogSpeed * s,
    dogCharge: b.dogCharge * s,
    dogAway: faster(b.dogAway),
    mouseEvery: faster(b.mouseEvery),
    mouseSpeed: b.mouseSpeed * s,
    windowOpenChance: b.windowOpenChance,
    windowOpenTicks: faster(b.windowOpenTicks),
    maxOpen: b.maxOpen,
    junkEvery: faster(b.junkEvery),
    rivalPatience: Math.round(b.rivalPatience / s),
    lineDrift: b.lineDrift * s,
    broomSpeed: b.broomSpeed * s,
    broomCooldown: Math.round(b.broomCooldown / s),
    roomMouseEvery: faster(b.roomMouseEvery),
    mousePeek: faster(b.mousePeek),
    cheeseMice: b.cheeseMice,
    spiderSpeed: b.spiderSpeed * s,
    spiderRest: Math.round(b.spiderRest / s),
    cageNudges: b.cageNudges,
    birdSpeed: b.birdSpeed * s,
    wakeRate: b.wakeRate * s,
    wakeDecay: b.wakeDecay,
    awakeTicks: Math.round(b.awakeTicks * s),
    drinkTicks: b.drinkTicks,
    refillEvery: faster(b.refillEvery),
    fishCount: b.fishCount,
    fishSpeed: b.fishSpeed * s,
    eelCount: b.eelCount,
    eelSpeed: b.eelSpeed * s,
    airTicks: Math.round(b.airTicks / s),
    cupidEvery: faster(b.cupidEvery),
    arrowSpeed: b.arrowSpeed * s,
    brotherSpeed: b.brotherSpeed * s,
    giftEvery: b.giftEvery,
    bribeTicks: Math.round(b.bribeTicks / s),
    brothers: b.brothers,
  };
}
